import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardMedia,
    Grid,
    IconButton,
    Chip,
    Alert,
    CircularProgress,
    Tooltip,
    useTheme,
    alpha,
    TablePagination,
    InputAdornment,
    Stack,
    Avatar,
    Fade,
    Zoom,
    Paper,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Save,
    Cancel,
    CloudUpload,
    Visibility,
    School,
    Search,
    FilterList,
    Refresh,
    Sort,
    ViewModule,
    ViewList,
    Category,
    PhotoLibrary,
    ContentCopy,
    ExpandMore,
    ExpandLess,
    TrendingUp
} from '@mui/icons-material';
import { 
    getAllTipsForAdmin,
    createTip,
    updateTip, 
    deleteTip, 
    createTipWithImage,
    uploadTipImage
} from '../../api/tips';
import { getAllCategories } from '../../api/categories';
import TipEditDialog from './TipEditDialog';
import api from '../../api/auth';

const AdminTips = () => {
    const theme = useTheme();
    const [tips, setTips] = useState([]);
    const [filteredTips, setFilteredTips] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTip, setEditingTip] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [currentTab, setCurrentTab] = useState(0); // 0: All, 1: By Category

    // Pagination & Filtering
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(12);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [sortBy, setSortBy] = useState('newest');

    // Form states
    const [formData, setFormData] = useState({
        title: '',
        tipText: '',
        categoryId: '',
        imageUrl: '',
        isActive: true
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        fetchTips();
        fetchCategories();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [tips, searchTerm, selectedCategory, statusFilter, sortBy]);

    const fetchTips = async () => {
        try {
            const response = await getAllTipsForAdmin();
            console.log('Raw response from admin endpoint:', response.data);

            // Debug: Check how many tips we have and their active status
            const allTips = response.data || [];
            console.log('Total tips fetched:', allTips.length);
            console.log('Active tips:', allTips.filter(tip => tip.isActive === true).length);
            console.log('Inactive tips:', allTips.filter(tip => tip.isActive === false).length);
            console.log('Tips with undefined isActive:', allTips.filter(tip => tip.isActive === undefined).length);

            // Debug: Log some sample tips to see their structure
            allTips.slice(0, 5).forEach((tip, index) => {
                console.log(`Tip ${index + 1}:`, {
                    id: tip.id,
                    title: tip.title,
                    isActive: tip.isActive,
                    isActiveType: typeof tip.isActive
                });
            });

            setTips(allTips);
        } catch (err) {
            setError('Failed to fetch tips');
            console.error('Error fetching tips:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            // Use the admin endpoint for waste categories
            const response = await api.get('/api/admin/waste-categories');
            setCategories(response.data || []);
        } catch (err) {
            console.error('Failed to fetch categories:', err);
            // If API fails, create default categories for the dropdown
            setCategories([
                { id: 1, name: 'Compost', color: '#4caf50' },
                { id: 2, name: 'General_Waste', color: '#9e9e9e' },
                { id: 3, name: 'Recycle', color: '#2196f3' },
                { id: 4, name: 'Hazardous', color: '#f44336' },
                { id: 5, name: 'Unknown', color: '#ff9800' }
            ]);
        }
    };

    const applyFilters = () => {
        let filtered = [...tips];

        console.log('Starting applyFilters with:', {
            totalTips: tips.length,
            statusFilter,
            searchTerm,
            selectedCategory
        });

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(tip =>
                tip.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                tip.tipText?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            console.log('After search filter:', filtered.length);
        }

        // Category filter
        if (selectedCategory) {
            filtered = filtered.filter(tip => tip.category?.id === parseInt(selectedCategory));
            console.log('After category filter:', filtered.length);
        }

        // Status filter
        if (statusFilter !== 'ALL') {
            const isActive = statusFilter === 'ACTIVE';
            console.log('Filtering by status:', { statusFilter, isActive });

            const beforeFilter = filtered.length;
            filtered = filtered.filter(tip => {
                const tipIsActive = Boolean(tip.isActive);
                const matches = tipIsActive === isActive;
                console.log(`Tip ${tip.id}: isActive=${tip.isActive} (${typeof tip.isActive}), converted=${tipIsActive}, matches=${matches}`);
                return matches;
            });
            console.log(`After status filter: ${beforeFilter} → ${filtered.length}`);
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                case 'oldest':
                    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                case 'title':
                    return (a.title || '').localeCompare(b.title || '');
                case 'category':
                    return (a.category?.name || '').localeCompare(b.category?.name || '');
                default:
                    return 0;
            }
        });

        console.log('Final filtered tips:', filtered.length);
        setFilteredTips(filtered);
    };

    const handleOpenDialog = (tip = null) => {
        if (tip) {
            setEditingTip(tip);
            setFormData({
                title: tip.title || '',
                tipText: tip.tipText || '',
                categoryId: tip.category?.id || '',
                imageUrl: tip.imageUrl || '',
                isActive: tip.isActive !== undefined ? tip.isActive : true
            });
            setImagePreview(tip.imageUrl || '');
        } else {
            setEditingTip(null);
            setFormData({
                title: '',
                tipText: '',
                categoryId: '',
                imageUrl: '',
                isActive: true
            });
            setImagePreview('');
        }
        setSelectedImage(null);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingTip(null);
        setFormData({
            title: '',
            tipText: '',
            categoryId: '',
            imageUrl: '',
            isActive: true
        });
        setSelectedImage(null);
        setImagePreview('');
        setError('');
        setSuccess('');
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                setError('Please select a valid image file (PNG, JPG, JPEG, WEBP, or GIF)');
                return;
            }

            // Validate file size (5MB max)
            if (file.size > 5 * 1024 * 1024) {
                setError('Image file size should be less than 5MB');
                return;
            }

            setSelectedImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async () => {
        if (!formData.tipText.trim()) {
            setError('Tip text is required');
            return;
        }

        setUploading(true);
        setError('');

        try {
            const tipData = {
                title: formData.title.trim() || null,
                tipText: formData.tipText.trim(),
                categoryId: formData.categoryId || null,
                isActive: formData.isActive
            };

            let response;
            
            if (editingTip) {
                // Update existing tip
                if (selectedImage) {
                    // Upload new image first
                    const imageResponse = await uploadTipImage(selectedImage);
                    tipData.imageUrl = imageResponse.data;
                } else if (formData.imageUrl) {
                    tipData.imageUrl = formData.imageUrl;
                }
                response = await updateTip(editingTip.id, tipData);
                setSuccess('Tip updated successfully!');
            } else {
                // Create new tip
                if (selectedImage) {
                    response = await createTipWithImage(tipData, selectedImage);
                } else {
                    response = await createTip(tipData);
                }
                setSuccess('Tip created successfully!');
            }

            await fetchTips();
            handleCloseDialog();

        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save tip');
            console.error('Error saving tip:', err);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (tipId) => {
        if (!window.confirm('Are you sure you want to delete this tip?')) {
            return;
        }

        try {
            await deleteTip(tipId);
            setSuccess('Tip deleted successfully!');
            await fetchTips();
        } catch (err) {
            setError('Failed to delete tip');
            console.error('Error deleting tip:', err);
        }
    };

    const handleDuplicate = (tip) => {
        handleOpenDialog({
            ...tip,
            id: null,
            title: `${tip.title} (Copy)`,
        });
    };

    const getCategoryColor = (categoryName) => {
        const colors = {
            'Compost': '#4caf50',
            'Recycle': '#2196f3',
            'Hazardous': '#f44336',
            'General_Waste': '#9e9e9e',
            'E-Waste': '#ff9800'
        };
        return colors[categoryName] || theme.palette.primary.main;
    };

    const paginatedTips = filteredTips.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

    const statsData = {
        total: tips.length,
        active: tips.filter(t => {
            // Handle different boolean representations - be more explicit about what constitutes "active"
            const isActive = t.isActive === true || t.isActive === 1 || t.isActive === '1' || t.isActive === 'true';
            console.log(`Stats calc - Tip ${t.id}: isActive=${t.isActive} (${typeof t.isActive}), evaluated=${isActive}`);
            return isActive;
        }).length,
        inactive: tips.filter(t => {
            // Handle different boolean representations for inactive
            const isActive = t.isActive === true || t.isActive === 1 || t.isActive === '1' || t.isActive === 'true';
            return !isActive;
        }).length,
        withImages: tips.filter(t => t.imageUrl).length,
        byCategory: categories.map(cat => ({
            name: cat.name,
            count: tips.filter(t => t.category?.id === cat.id).length,
            color: getCategoryColor(cat.name)
        }))
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Fade in={loading} timeout={500}>
                    <Stack alignItems="center" spacing={3}>
                        <Zoom in={true} timeout={500}>
                            <Avatar sx={{
                                width: 80,
                                height: 80,
                                background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%': { transform: 'scale(1)' },
                                    '50%': { transform: 'scale(1.1)' },
                                    '100%': { transform: 'scale(1)' }
                                }
                            }}>
                                <School sx={{ fontSize: 40, color: 'white' }} />
                            </Avatar>
                        </Zoom>
                        <Typography variant="h6" fontWeight={600} color="text.secondary">
                            Loading Educational Tips...
                        </Typography>
                    </Stack>
                </Fade>
            </Box>
        );
    }

    return (
        <Box>
            {/* Enhanced Header with Statistics */}
            <Paper elevation={6} sx={{
                mb: 4,
                p: 4,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    animation: 'float 6s ease-in-out infinite',
                    '@keyframes float': {
                        '0%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-20px)' },
                        '100%': { transform: 'translateY(0px)' }
                    }
                }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                            <Avatar sx={{
                                background: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                border: '3px solid rgba(255,255,255,0.3)',
                                width: 80,
                                height: 80
                            }}>
                                <School sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h3" fontWeight={800} sx={{ mb: 1 }}>
                                    📚 Educational Tips Hub
                                </Typography>
                                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                    Advanced Content Management System
                                </Typography>
                            </Box>
                        </Box>

                        {/* Statistics Overview */}
                        <Grid container spacing={2}>
                            <Grid item xs={6} md={3}>
                                <Card sx={{ p: 2, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                        Total Tips
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                                        {statsData.total}
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Card sx={{ p: 2, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                        Active Tips
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                                        {statsData.active}
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Card sx={{ p: 2, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                        Inactive Tips
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                                        {statsData.inactive}
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Card sx={{ p: 2, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                        With Images
                                    </Typography>
                                    <Typography variant="h5" fontWeight={700} sx={{ color: 'white' }}>
                                        {statsData.withImages}
                                    </Typography>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Stack spacing={2}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => handleOpenDialog()}
                                sx={{
                                    background: 'rgba(255,255,255,0.2)',
                                    '&:hover': { background: 'rgba(255,255,255,0.3)' },
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    py: 1.5
                                }}
                                fullWidth
                                size="large"
                            >
                                Create New Tip
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<Refresh />}
                                onClick={fetchTips}
                                sx={{
                                    borderColor: 'rgba(255,255,255,0.5)',
                                    color: 'white',
                                    '&:hover': {
                                        borderColor: 'white',
                                        background: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                                fullWidth
                            >
                                Refresh Data
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Advanced Filters & Controls */}
            <Paper elevation={3} sx={{ mb: 4, p: 3, borderRadius: 3 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            placeholder="Search tips by title or content..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ backgroundColor: '#f8f9fa', borderRadius: 2 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={selectedCategory}
                                label="Category"
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={statusFilter}
                                label="Status"
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <MenuItem value="ALL">All Status</MenuItem>
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="INACTIVE">Inactive</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                value={sortBy}
                                label="Sort By"
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <MenuItem value="newest">Newest First</MenuItem>
                                <MenuItem value="oldest">Oldest First</MenuItem>
                                <MenuItem value="title">Title A-Z</MenuItem>
                                <MenuItem value="category">Category</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Grid View">
                                <IconButton
                                    onClick={() => setViewMode('grid')}
                                    color={viewMode === 'grid' ? 'primary' : 'default'}
                                >
                                    <ViewModule />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="List View">
                                <IconButton
                                    onClick={() => setViewMode('list')}
                                    color={viewMode === 'list' ? 'primary' : 'default'}
                                >
                                    <ViewList />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Tips Display */}
            {filteredTips.length === 0 ? (
                <Card sx={{ textAlign: 'center', py: 8 }}>
                    <CardContent>
                        <School sx={{ fontSize: 64, color: theme.palette.grey[400], mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            {searchTerm || selectedCategory || statusFilter !== 'ALL'
                                ? 'No tips match your filters'
                                : 'No educational tips found'
                            }
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {searchTerm || selectedCategory || statusFilter !== 'ALL'
                                ? 'Try adjusting your search or filter criteria'
                                : 'Start by creating your first educational tip'
                            }
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenDialog()}
                        >
                            Create First Tip
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {viewMode === 'grid' ? (
                        // Grid View
                        <Grid container spacing={3}>
                            {paginatedTips.map((tip) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={tip.id}>
                                    <Card
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            opacity: tip.isActive ? 1 : 0.7,
                                            border: tip.isActive ? 'none' : `2px dashed ${theme.palette.grey[400]}`,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: theme.shadows[8]
                                            }
                                        }}
                                    >
                                        {tip.imageUrl && (
                                            <CardMedia
                                                component="img"
                                                height="160"
                                                image={tip.imageUrl}
                                                alt={tip.title || 'Educational tip'}
                                                sx={{ objectFit: 'cover' }}
                                            />
                                        )}
                                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                {tip.title && (
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, lineHeight: 1.3 }}>
                                                        {tip.title.length > 50 ? `${tip.title.substring(0, 50)}...` : tip.title}
                                                    </Typography>
                                                )}
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                    <Tooltip title="Edit tip">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDialog(tip)}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Duplicate tip">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDuplicate(tip)}
                                                        >
                                                            <ContentCopy fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete tip">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete(tip.id)}
                                                            color="error"
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    mb: 2,
                                                    lineHeight: 1.4,
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 3,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden'
                                                }}
                                            >
                                                {tip.tipText}
                                            </Typography>

                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                {tip.category && (
                                                    <Chip
                                                        label={tip.category.name}
                                                        size="small"
                                                        sx={{
                                                            background: alpha(getCategoryColor(tip.category.name), 0.1),
                                                            color: getCategoryColor(tip.category.name),
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    />
                                                )}
                                                <Chip
                                                    label={tip.isActive ? 'Active' : 'Inactive'}
                                                    size="small"
                                                    color={tip.isActive ? 'success' : 'default'}
                                                    variant={tip.isActive ? 'filled' : 'outlined'}
                                                    sx={{ fontSize: '0.7rem' }}
                                                />
                                                {tip.imageUrl && (
                                                    <Chip
                                                        icon={<PhotoLibrary />}
                                                        label="Has Image"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        // List View
                        <Stack spacing={2}>
                            {paginatedTips.map((tip) => (
                                <Card key={tip.id} sx={{ p: 3 }}>
                                    <Grid container spacing={3} alignItems="center">
                                        {tip.imageUrl && (
                                            <Grid item xs={12} md={2}>
                                                <img
                                                    src={tip.imageUrl}
                                                    alt={tip.title || 'Educational tip'}
                                                    style={{
                                                        width: '100%',
                                                        height: 80,
                                                        objectFit: 'cover',
                                                        borderRadius: 8
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12} md={tip.imageUrl ? 8 : 10}>
                                            <Box>
                                                {tip.title && (
                                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                                        {tip.title}
                                                    </Typography>
                                                )}
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mb: 2, lineHeight: 1.5 }}
                                                >
                                                    {tip.tipText.length > 200
                                                        ? `${tip.tipText.substring(0, 200)}...`
                                                        : tip.tipText
                                                    }
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {tip.category && (
                                                        <Chip
                                                            label={tip.category.name}
                                                            size="small"
                                                            sx={{
                                                                background: alpha(getCategoryColor(tip.category.name), 0.1),
                                                                color: getCategoryColor(tip.category.name),
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    )}
                                                    <Chip
                                                        label={tip.isActive ? 'Active' : 'Inactive'}
                                                        size="small"
                                                        color={tip.isActive ? 'success' : 'default'}
                                                        variant={tip.isActive ? 'filled' : 'outlined'}
                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                <Tooltip title="Edit tip">
                                                    <IconButton onClick={() => handleOpenDialog(tip)}>
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Duplicate tip">
                                                    <IconButton onClick={() => handleDuplicate(tip)}>
                                                        <ContentCopy />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete tip">
                                                    <IconButton
                                                        onClick={() => handleDelete(tip.id)}
                                                        color="error"
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Card>
                            ))}
                        </Stack>
                    )}

                    {/* Enhanced Pagination */}
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                        <Paper elevation={3} sx={{ borderRadius: 3 }}>
                            <TablePagination
                                component="div"
                                count={filteredTips.length}
                                page={page}
                                onPageChange={(event, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(event) => {
                                    setRowsPerPage(parseInt(event.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={[6, 12, 24, 48]}
                                showFirstButton
                                showLastButton
                                sx={{ borderRadius: 3 }}
                            />
                        </Paper>
                    </Box>
                </>
            )}

            {/* Enhanced Create/Edit Dialog */}
            <TipEditDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                tip={editingTip}
                categories={categories}
                onSave={async (tipData) => {
                    try {
                        let response;

                        if (editingTip) {
                            // Update existing tip
                            response = await updateTip(editingTip.id, tipData);
                            setSuccess('Tip updated successfully!');
                        } else {
                            // Create new tip
                            if (tipData.imageUrl) {
                                // If image URL is provided, use regular create
                                response = await createTip(tipData);
                            } else {
                                response = await createTip(tipData);
                            }
                            setSuccess('Tip created successfully!');
                        }

                        await fetchTips();
                        handleCloseDialog();

                    } catch (err) {
                        throw new Error(err.response?.data?.message || 'Failed to save tip');
                    }
                }}
                loading={uploading}
            />
        </Box>
    );
};

export default AdminTips;

